import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DialogV2, DrawerV2, Flex, JoyKitThemeProvider, Box } from '@withjoy/joykit';
import { FiltersPanel } from './components/FiltersPanel/FiltersPanel';
import { ListingsPanel } from './components/ListingsPanel/ListingsPanel';
import { Map } from './components/Map/Map';
import { ListButton } from './components/Map/components/ListButton';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useListingFilters } from './hooks/useListingFilters';
import { useListingSort } from './hooks/useListingSort';
import { useListingSortingByViewport } from './hooks/useListingSortingByViewport';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { AccommodationsConsoleDialogProps, AccommodationsConsoleProps, AccommodationsConsoleState, AccommodationListingDTO } from './AccommodationsConsole.types';
import { useAccommodationsConsoleListings } from './hooks/useAccommodationsConsoleListings';
import { AccommodationsConsoleHeader } from './components/Header';
import { EmptyState } from './components/EmptyState';
import { differenceInCalendarDays } from 'date-fns';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { usePagination } from './hooks/usePagination';
import { ShowMoreButton } from './components/ListingsPanel';
import { MobileSearchComponent } from './components/MobileSearchBar';
import { SingleHotelView } from './components/SingleHotelView/SingleHotelView';
import { BottomSheet } from './components/BottomSheet';
import { useAccommodationsConsoleHealth, wrapWithAccommodationsConsoleProvider } from './AccommodationsConsole.health';
import { UseAccommodationsConsoleSearchParamsStateReturn, useAccommodationsConsoleSearchParamsState } from './hooks/useAccommodationsConsoleSearchParamsState';
import { useMobileSheetDisclosures } from './hooks/useMobileSheetDisclosures';
import { isValidCoordinates } from '@shared/core/places';
import { useMapTelemetry } from './components/Map/Map.telemetry';
import { Map as MapIcon } from '@withjoy/joykit/icons';

import { useMapControls } from './hooks/useMapControls';
import { MapLoader } from './components/Map/MapLoader';
import { useAccommodationRedirectUrl } from './hooks/useAccommodationRedirectUrl';
import { MobileTransparentDrawer } from '@shared/components/MobileTransparentDrawer';
import { useTranslation } from '@shared/core';
import { SortOption } from './AccommodationsConsole.types';
import { useClearAccommodationConsoleSearchParamsOnUnmount } from './hooks/useClearAccommodationConsoleSearchParamsOnUnmount';
import { useListingsPanelTelemetry } from './components/ListingsPanel/ListingsPanel.telemetry';
import { AccommodationsConsoleLoader } from './components/AccommodationsConsoleLoader/AccommodationsConsoleLoader';
import { ProgressiveLoader } from './components/ProgressiveLoader';
import { withWindow } from '@shared/utils/withWindow';
import { useAccommodationsConsoleTelemetry } from './AccommodationsConsole.telemetry';
import { useMount } from '@shared/utils/hooks/useMount';
import { AccommodationMapListSync } from './components/AccommodationMapListSync/AccommodationMapListSync';
import { useAccommodationsConsoleConfig, MapLayoutMode } from './AccommodationsConsoleConfigContext';
import { LatLngLiteral } from '@shared/hooks/useGooglePlaceAPI';

const Root = ({
  state,
  eventName,
  onClose,
  onChangeLocation,
  onHeaderSearchSubmit,
  selectedHotelId,
  isInitialLoad,
  isShowingAllCouplesPicks,
  shouldPrioritizeParams,
  eventHandle,
}: {
  state: AccommodationsConsoleState;
  onChangeLocation: UseAccommodationsConsoleSearchParamsStateReturn['handleOnChangeLocation'];
  onHeaderSearchSubmit: UseAccommodationsConsoleSearchParamsStateReturn['handleHeaderSearchFormSubmit'];
  selectedHotelId?: string | null;
  isInitialLoad: boolean;
  isShowingAllCouplesPicks: boolean;
} & AccommodationsConsoleProps) => {
  const telemetry = useMapTelemetry();
  const { isFilterPanelOpen, onFilterPanelOpen, onFilterPanelClose, isSearchOpen, onSearchOpen, onSearchClose } = useMobileSheetDisclosures();
  const { tq } = useTranslation('accommodationsConsole');

  const isMobileOrTablet = useIsMobileOrTablet();
  const { onLoadComplete, onLoadFailure } = useAccommodationsConsoleHealth();
  const config = useAccommodationsConsoleConfig();
  const [hidePanel, togglePanel] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState<AccommodationListingDTO | null>(null);
  const [isSingleViewOpen, setIsSingleViewOpen] = useState(false);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const { mapViewPort, setMapViewPort, reCenterMap, recenterMapViaLocation, setReCenterMap, recenterMapViaMarkers } = useMapControls();

  const isCompactMapMode = !isMobileOrTablet && config.layout?.mapLayout.mode === MapLayoutMode.COMPACT;
  const shouldShowFullMap = isMapExpanded || !isCompactMapMode;
  const skipCouplePicks = config.layout.skipCouplePicks ?? false;

  // If skipCouplePicks is true or we're in compact mode (where map is hidden initially),
  // disable couple picks mode to ensure consistent behavior
  const effectiveIsShowingAllCouplesPicks = skipCouplePicks || isCompactMapMode ? false : isShowingAllCouplesPicks;

  const { cachedListings, listings, isFetchingListings, isFetchingListingPrices, pricingError, isCombinedLoading, isUsingBackgroundListings } = useAccommodationsConsoleListings({
    state,
    variant: effectiveIsShowingAllCouplesPicks ? 'couple' : 'regional',
    skipCouplePicks: skipCouplePicks,
    onError: error => {
      onLoadFailure(error, { state, filters });
    },
    onSuccess: () => {
      onLoadComplete();
    },
    isInitialLoad,
    shouldPrioritizeParams,
  });

  const { filters, filteredProperties, updateFilters, supportedFacilities, facilityCounts, starRatingCounts, guestRatingCounts, resetFilters } = useListingFilters(
    listings,
    isFetchingListingPrices,
  );

  const sortListingsInViewport = useListingSortingByViewport();

  // Filter hotels as per map visible port
  const listingsInViewport = useMemo(() => {
    if (shouldShowFullMap && !isFetchingListings && isUsingBackgroundListings && isInitialLoad) {
      return sortListingsInViewport(mapViewPort, filteredProperties);
    }
    // When map is not shown, using cached data, or user has searched, return all filtered properties
    return filteredProperties;
  }, [mapViewPort, sortListingsInViewport, filteredProperties, shouldShowFullMap, isFetchingListings, isUsingBackgroundListings, isInitialLoad]);

  // Apply sorting based on selected sort option
  const { sortedListings, sortOption, updateSort } = useListingSort(listingsInViewport, state.venue, filters);

  const handleSearchFormSubmit = useEventCallback<UseAccommodationsConsoleSearchParamsStateReturn['handleHeaderSearchFormSubmit']>(value => {
    onHeaderSearchSubmit(value);
    resetFilters();
    recenterMapViaLocation(true);
  });

  /**
   * Generates map markers for the accommodation map to show the couple picks initially.
   * When showing all couples picks, includes:
   * - Markers for all hotels marked as picked by the couple
   * - The search center location marker (if a circular region search is active)
   * Returns an empty array when not showing couples picks.
   */
  const mapMarkers = useMemo(() => {
    if (!effectiveIsShowingAllCouplesPicks) {
      return [];
    }

    // Collect markers for all hotels picked by the couple
    const couplePickMarkers: ReadonlyArray<LatLngLiteral> = filteredProperties.reduce<ReadonlyArray<LatLngLiteral>>(
      (markers, hotel) => (hotel.isPickedByCouple ? [...markers, { lat: hotel.location.lat, lng: hotel.location.lng }] : markers),
      [] as ReadonlyArray<LatLngLiteral>,
    );

    // Add the search center location marker if available
    const searchCenterMarker: ReadonlyArray<LatLngLiteral> = state.circularRegionSearch
      ? [{ lat: state.circularRegionSearch.latitude, lng: state.circularRegionSearch.longitude }]
      : ([] as ReadonlyArray<LatLngLiteral>);

    return [...couplePickMarkers, ...searchCenterMarker] as const;
  }, [filteredProperties, effectiveIsShowingAllCouplesPicks, state.circularRegionSearch]);

  useEffect(() => {
    if (effectiveIsShowingAllCouplesPicks) {
      recenterMapViaMarkers();
    } else {
      recenterMapViaLocation();
    }
  }, [effectiveIsShowingAllCouplesPicks, recenterMapViaMarkers, recenterMapViaLocation]);

  const onChangeMapViewPort = useEventCallback((center: { lat: number; lng: number }, bounds: google.maps.LatLngBounds) => {
    setMapViewPort({ center, bounds });
  });

  const getRedirectUrl = useAccommodationRedirectUrl(state);
  const listingTelemetry = useListingsPanelTelemetry();

  const scrollToSelectedHotel = useEventCallback((hotelId: Maybe<string>) => {
    const hotelElement = document.getElementById(`listing-${hotelId}`);
    if (hotelElement) {
      hotelElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  const onClickMarker = useEventCallback((hotel: AccommodationListingDTO) => {
    telemetry.mapMarkerClicked({
      listingType: hotel.type,
      placeId: hotel.joyPlaceId,
      hotelName: hotel.name,
      from: 'fullMap',
    });
    setSelectedHotel(hotel);
  });

  const handleOnTogglePanel = useEventCallback(() => {
    togglePanel(isOpen => !isOpen);
  });

  const handleResetFilters = useEventCallback(() => {
    resetFilters();
    updateSort(SortOption.COUPLES_PICKS);
    recenterMapViaLocation();
  });

  const noOfNights = useMemo(() => differenceInCalendarDays(new Date(state.searchDates.checkout), new Date(state.searchDates.checkin)), [state.searchDates]);

  const { paginatedListings, showNextPageOfListings, hasMoreListingsToShow, listingsContainerRef } = usePagination({ listings: sortedListings, filters });

  const handleHotelSelection = useEventCallback(
    (hotel: AccommodationListingDTO) => {
      if (isMobileOrTablet) {
        setIsSingleViewOpen(true);
      } else if (hidePanel) {
        const redirectUrl = getRedirectUrl(hotel);
        if (!redirectUrl) {
          return;
        }
        listingTelemetry.seeAvailabilityClicked({ joyPlaceId: hotel?.joyPlaceId || '', hotelName: hotel?.name || '' });

        withWindow(global => {
          global.open(redirectUrl, '_blank');
        });
      } else {
        scrollToSelectedHotel(hotel.joyPlaceId);
      }
    },
    [isMobileOrTablet, setSelectedHotel, scrollToSelectedHotel, getRedirectUrl, listingTelemetry, hidePanel],
  );

  useEffect(() => {
    if (selectedHotel) {
      handleHotelSelection(selectedHotel);
      if (!isMobileOrTablet) {
        setSelectedHotel(null);
      }
    }
  }, [handleHotelSelection, isMobileOrTablet, selectedHotel]);

  useEffect(() => {
    if (selectedHotelId) {
      const hotel = sortedListings.find((h: AccommodationListingDTO) => h.joyPlaceId === selectedHotelId);
      if (!hotel) return;
      setSelectedHotel(hotel);
    }
  }, [selectedHotelId, sortedListings]);

  const handleShowOnMap = useEventCallback(() => {
    setIsMapExpanded(true);
  });

  const handleShowList = useCallback(() => {
    setIsMapExpanded(false);
    if (hidePanel) {
      togglePanel(false);
    }
  }, [hidePanel, togglePanel]);

  // Reset map zoom to default when map is expanded from compact mode
  // This ensures the zoom is set even if auto-zoom happens after initial render
  useEffect(() => {
    if (isMapExpanded && isCompactMapMode) {
      // Use a delay to ensure the map is fully rendered and any auto-zoom has completed
      // before resetting to default zoom again
      const timer = setTimeout(() => {
        recenterMapViaLocation(true);
      }, 600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isMapExpanded, isCompactMapMode, recenterMapViaLocation]);

  const mapLocation =
    state.circularRegionSearch && isValidCoordinates({ latitude: state.circularRegionSearch.latitude, longitude: state.circularRegionSearch.longitude })
      ? { latlng: { lat: state.circularRegionSearch.latitude, lng: state.circularRegionSearch.longitude } }
      : null;

  const filtersPanelContent = (
    <SkeletonGroup
      // Keeping the code assuming that might be needed in the future
      isReady={true}
      placeholder={<FiltersPanel.Skeleton />}
    >
      <FiltersPanel
        filters={filters}
        eventName={eventName}
        onFiltersChange={updateFilters}
        supportedFacilities={supportedFacilities}
        facilityCounts={facilityCounts}
        starRatingCounts={starRatingCounts}
        prices={listings.map(listing => listing.pricingInfo?.values?.nightlyBaseRate).filter((price): price is number => price != null && Number.isFinite(price) && price >= 0)}
        guestRatingCounts={guestRatingCounts}
        isLoadingPrices={isFetchingListingPrices}
        hidePriceRangeFacet={!!pricingError}
        onClose={onFilterPanelClose}
        onReset={handleResetFilters}
        sortOption={sortOption}
        onSortChange={updateSort}
        mapData={
          isCompactMapMode && mapLocation
            ? {
                location: mapLocation,
                onShowOnMap: handleShowOnMap,
                shouldShowFullMap: shouldShowFullMap,
              }
            : undefined
        }
      />
    </SkeletonGroup>
  );

  const listingsPanelContent = (
    <SkeletonGroup
      // Keeping the code assuming that might be needed in the future
      isReady={!isFetchingListings}
      overflowY={{ _: undefined, md: 'auto' }}
      width={{ _: '100%', md: hidePanel ? 0 : 'fit-content' }}
      paddingBottom={9}
      placeholder={<ListingsPanel.Skeleton isMobileOrTablet={isMobileOrTablet} />}
      rootRef={listingsContainerRef}
    >
      {paginatedListings.length > 0 ? (
        <>
          <ListingsPanel
            state={state}
            sortOption={sortOption}
            onSortChange={updateSort}
            noOfNights={noOfNights}
            filters={filters}
            updateFilters={updateFilters}
            listings={paginatedListings}
            isLoadingPrices={isFetchingListingPrices}
            hidePanel={hidePanel}
          />
          {hasMoreListingsToShow && <ShowMoreButton onClick={showNextPageOfListings} />}
        </>
      ) : (
        <EmptyState
          onReset={handleResetFilters}
          showResetButton={!!cachedListings?.length}
        />
      )}
    </SkeletonGroup>
  );

  const mapContent =
    state.circularRegionSearch && isValidCoordinates({ latitude: state.circularRegionSearch.latitude, longitude: state.circularRegionSearch.longitude }) ? (
      <Map
        location={{ latlng: { lat: state.circularRegionSearch.latitude, lng: state.circularRegionSearch.longitude } }}
        // Passing an empty array when `isFetchingListings` so that we dont see marker artifacts
        hotels={isFetchingListings ? [] : paginatedListings}
        isFetchingHotels={isFetchingListings}
        onChangeLocation={onChangeLocation}
        onChangeMapViewPort={onChangeMapViewPort}
        onClickMarker={onClickMarker}
        isCouplePick={effectiveIsShowingAllCouplesPicks}
        onToggleLeftPanel={handleOnTogglePanel}
        setReCenterMap={setReCenterMap}
        selectedMarker={selectedHotel?.joyPlaceId || ''}
        reCenterMap={reCenterMap}
        hidePanel={hidePanel}
        mapMarkers={isFetchingListings ? [] : mapMarkers}
        venue={state.venue}
        shouldShowVenueMapPin={config.map?.shouldShowVenueMapPin}
      />
    ) : null;

  return (
    <Container>
      <AccommodationsConsoleHeader
        handleFormSubmit={handleSearchFormSubmit}
        state={state}
        onFilterPanelOpen={onFilterPanelOpen}
        onSearchOpen={onSearchOpen}
        onClose={onClose}
      />
      {!isMobileOrTablet && <ProgressiveLoader isLoading={isCombinedLoading} />}
      {/* Main Content */}
      {isMobileOrTablet ? (
        <AccommodationMapListSync>
          <Content>
            {state.circularRegionSearch && isValidCoordinates({ latitude: state.circularRegionSearch.latitude, longitude: state.circularRegionSearch.longitude }) ? (
              <Map
                location={{ latlng: { lat: state.circularRegionSearch.latitude, lng: state.circularRegionSearch.longitude } }}
                venue={state.venue}
                // Passing an empty array when `isFetchingListings` so that we dont see marker artifacts
                hotels={isFetchingListings ? [] : paginatedListings}
                isFetchingHotels={isFetchingListings}
                onChangeLocation={onChangeLocation}
                onChangeMapViewPort={onChangeMapViewPort}
                onClickMarker={onClickMarker}
                isCouplePick={effectiveIsShowingAllCouplesPicks}
                onToggleLeftPanel={handleOnTogglePanel}
                setReCenterMap={setReCenterMap}
                selectedMarker={selectedHotel?.joyPlaceId || ''}
                reCenterMap={reCenterMap}
                hidePanel={hidePanel}
                mapMarkers={isFetchingListings ? [] : mapMarkers}
                shouldShowVenueMapPin={config.map?.shouldShowVenueMapPin}
              />
            ) : null}
            {isFetchingListings && !state.circularRegionSearch && <MapLoader />}
          </Content>
          <MobileTransparentDrawer
            scrollTopCta={{ icon: MapIcon, text: tq`Map`, closeOnClick: true }}
            isOpenByDefault={true}
            animationType="spring"
          >
            {listingsPanelContent}
          </MobileTransparentDrawer>
          <BottomSheet
            isOpen={isFilterPanelOpen}
            onClose={onFilterPanelClose}
          >
            {filtersPanelContent}
          </BottomSheet>
          <BottomSheet
            isOpen={isSearchOpen}
            onClose={onSearchClose}
          >
            <MobileSearchComponent
              onClose={onSearchClose}
              state={state}
              handleFormSubmit={handleSearchFormSubmit}
            />
          </BottomSheet>
          {selectedHotel && isSingleViewOpen && (
            <SingleHotelView
              onClose={() => {
                setIsSingleViewOpen(false);
                setSelectedHotel(null);
              }}
              listings={paginatedListings}
              selectedHotelId={selectedHotel?.joyPlaceId}
              state={state}
              noOfNights={noOfNights}
            />
          )}
        </AccommodationMapListSync>
      ) : (
        <Content>
          {isFetchingListings && isInitialLoad ? (
            <Flex
              width="100%"
              height={'100%'}
              justifyContent="center"
              alignItems="center"
            >
              <AccommodationsConsoleLoader />
            </Flex>
          ) : (
            <AccommodationMapListSync>
              {isCompactMapMode ? (
                <Flex
                  width="100%"
                  height="100%"
                  overflowY="auto"
                  justifyContent={shouldShowFullMap ? 'flex-start' : 'center'}
                  alignItems="flex-start"
                  transition="all 0.3s ease-in-out"
                  position="relative"
                >
                  <Box
                    position="sticky"
                    top={0}
                    alignSelf="flex-start"
                    height="fit-content"
                    maxHeight="100%"
                    overflowY="auto"
                    overflowX="hidden"
                    zIndex={1}
                  >
                    {filtersPanelContent}
                  </Box>
                  {listingsPanelContent}
                  {shouldShowFullMap && mapContent ? (
                    <Box
                      flex="1"
                      height="100%"
                      position="sticky"
                      top={0}
                      alignSelf="flex-start"
                      transition="all 0.3s ease-in-out"
                      style={{
                        animation: 'slideIn 0.3s ease-in-out',
                      }}
                    >
                      <Box
                        position="absolute"
                        top={7}
                        right={7}
                        zIndex="docked"
                      >
                        <ListButton onClick={handleShowList} />
                      </Box>
                      {mapContent}
                    </Box>
                  ) : null}
                </Flex>
              ) : (
                <>
                  {filtersPanelContent}
                  {listingsPanelContent}
                  {mapContent}
                </>
              )}
            </AccommodationMapListSync>
          )}
        </Content>
      )}
    </Container>
  );
};

const Container = ({ children }: { children?: React.ReactNode }) => {
  return <>{children}</>;
};

const Content = ({ children }: { children: React.ReactNode }) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  return (
    <Flex
      overflow="auto"
      height={isMobileOrTablet ? 'calc(100vh - 120px)' : '100%'}
      minHeight={isMobileOrTablet ? '400px' : 'auto'}
    >
      {children}
    </Flex>
  );
};

// this will likely become a provider
export const AccommodationsConsole = ({ eventHandle, eventName, viewMode = 'standalone', onClose, selectedHotelId, shouldPrioritizeParams }: AccommodationsConsoleProps) => {
  const { state, loading, handleOnChangeLocation, handleHeaderSearchFormSubmit, isInitialLoad, isShowingAllCouplesPicks } = useAccommodationsConsoleSearchParamsState({
    eventHandle,
    shouldPrioritizeParams,
  });
  const isMobileOrTablet = useIsMobileOrTablet();
  useClearAccommodationConsoleSearchParamsOnUnmount(viewMode);

  const { pageViewed } = useAccommodationsConsoleTelemetry();

  useMount(() => {
    pageViewed();
  });

  return (
    <SkeletonGroup
      isReady={!loading || !!state}
      flexDirection="column"
      width={'100%'}
      height={'100%'}
      maxWidth="100%"
      overflow="auto"
      borderRadius="inherit"
      display="flex"
      placeholder={
        <Container>
          <AccommodationsConsoleHeader.Skeleton />
          <Content>
            <FiltersPanel.Skeleton />
            <ListingsPanel.Skeleton isMobileOrTablet={isMobileOrTablet} />
          </Content>
        </Container>
      }
    >
      {state && (
        <Root
          state={state}
          eventHandle={eventHandle}
          eventName={eventName}
          onClose={onClose}
          onChangeLocation={handleOnChangeLocation}
          onHeaderSearchSubmit={handleHeaderSearchFormSubmit}
          selectedHotelId={selectedHotelId}
          isInitialLoad={isInitialLoad}
          isShowingAllCouplesPicks={isShowingAllCouplesPicks}
          shouldPrioritizeParams={shouldPrioritizeParams}
        />
      )}
    </SkeletonGroup>
  );
};

export const AccommodationsConsoleWithHealth = wrapWithAccommodationsConsoleProvider(AccommodationsConsole);

export const AccommodationsConsoleDialog = ({ isOpen, onClose, viewMode = 'standalone', ...restProps }: AccommodationsConsoleDialogProps) => {
  const isMobileOrTablet = useIsMobileOrTablet();

  const content = (
    <JoyKitThemeProvider>
      <AccommodationsConsoleWithHealth
        {...restProps}
        viewMode={viewMode}
        onClose={onClose}
      />
    </JoyKitThemeProvider>
  );

  if (isMobileOrTablet) {
    return (
      <DrawerV2
        isOpen={isOpen}
        overrides={{ Content: { props: { width: '100%', height: '100%', overflow: 'hidden' } } }}
        onClose={onClose}
        scrollBehavior="inside"
        disableFocusLockRemoveScroll
        anchor="bottom"
      >
        {content}
      </DrawerV2>
    );
  }

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      scrollBehavior="inside"
      disableFocusLockRemoveScroll
      overrides={{
        Content: {
          props: {
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          },
        },
        ContentContainer: {
          props: {
            width: '100%',
          },
        },
      }}
    >
      {content}
    </DialogV2>
  );
};
